import type { AppUser } from '@/lib/auth';
import { requireAdminOrManager } from '@/lib/auth/guards';
import { normalizeError, isServiceError } from '@/lib/services/errors';
import {
  archiveUnit,
  createUnit,
  deleteUnit,
  getUnit,
  getUnitSummary,
  listUnits,
  listPortfolioUnits,
  updateUnit,
} from '@/lib/supabase/units';
import type {
  PortfolioUnitsResult,
  UnitFormValues,
  UnitListFilters,
  UnitSummary,
  UnitsListResult,
} from '@/lib/types/property';
import { paginationSchema, unitFilterSchema, unitFormSchema } from '@/lib/validation/property';

export interface ListUnitsArgs {
  user: AppUser | null;
  propertyId: string;
  filters?: UnitListFilters;
  signal?: AbortSignal;
}

export interface ListPortfolioUnitsArgs {
  user: AppUser | null;
  propertyId?: string;
  filters?: UnitListFilters;
  signal?: AbortSignal;
  page?: number;
  pageSize?: number;
}

export const PORTFOLIO_UNITS_DEFAULT_PAGE = 1;
export const PORTFOLIO_UNITS_DEFAULT_PAGE_SIZE = 25;
export interface UnitDetailArgs {
  user: AppUser | null;
  propertyId: string;
  unitId: string;
}

export interface CreateUnitArgs {
  user: AppUser | null;
  propertyId: string;
  values: UnitFormValues;
}

export interface UpdateUnitArgs extends UnitDetailArgs {
  values: UnitFormValues;
}

export async function getUnitSummaryForUser({
  user,
  propertyId,
  unitId,
}: UnitDetailArgs): Promise<UnitSummary> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    return await getUnitSummary(propertyId, unitId);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function listUnitsForUser({
  user,
  propertyId,
  filters,
  signal,
}: ListUnitsArgs): Promise<UnitsListResult> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const safeFilters = filters ? unitFilterSchema.parse(filters) : undefined;
    return await listUnits({ propertyId, filters: safeFilters, signal });
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function listPortfolioUnitsForUser({
  user,
  propertyId,
  filters,
  signal,
  page,
  pageSize,
}: ListPortfolioUnitsArgs): Promise<PortfolioUnitsResult> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    const safeFilters = filters ? unitFilterSchema.parse(filters) : undefined;
    const safePagination = paginationSchema.parse({
      page: page ?? PORTFOLIO_UNITS_DEFAULT_PAGE,
      pageSize: pageSize ?? PORTFOLIO_UNITS_DEFAULT_PAGE_SIZE,
    });
    return await listPortfolioUnits({
      propertyId,
      filters: safeFilters,
      signal,
      page: safePagination.page,
      pageSize: safePagination.pageSize,
    });
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function getUnitForUser({
  user,
  propertyId,
  unitId,
}: UnitDetailArgs): Promise<UnitFormValues> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    return await getUnit(propertyId, unitId);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function createUnitForUser({
  user,
  propertyId,
  values,
}: CreateUnitArgs): Promise<UnitFormValues> {
  const actor = requireAdminOrManager(user);

  try {
    const payload = unitFormSchema.parse({ ...values, propertyId });
    return await createUnit(propertyId, payload, actor.id);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function updateUnitForUser({
  user,
  propertyId,
  unitId,
  values,
}: UpdateUnitArgs): Promise<UnitFormValues> {
  const actor = requireAdminOrManager(user);

  try {
    const payload = unitFormSchema.parse({ ...values, propertyId });
    return await updateUnit(propertyId, unitId, payload, actor.id);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function archiveUnitForUser({
  user,
  propertyId,
  unitId,
}: UnitDetailArgs): Promise<UnitSummary> {
  const actor = requireAdminOrManager(user);

  try {
    return await archiveUnit(propertyId, unitId, actor.id);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

export async function deleteUnitForUser({
  user,
  propertyId,
  unitId,
}: UnitDetailArgs): Promise<void> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    await deleteUnit(propertyId, unitId);
  } catch (error) {
    handleUnitServiceError(error);
  }
}

function handleUnitServiceError(error: unknown): never {
  if (isServiceError(error)) {
    throw error;
  }

  throw normalizeError(error);
}
